import { Box, Flex, Text, IconButton, Input, InputGroup, InputLeftElement, useColorModeValue, Button, Avatar, Menu, MenuButton, MenuList, MenuItem, useToast, useBreakpointValue, HStack, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, VStack, useDisclosure, Image } from '@chakra-ui/react';
import { SearchIcon, HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './NotificationBell';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../services/authService';
import lightLogo from '../assets/Vindie Stream plain inline.svg';
import darkLogo from '../assets/Vindie Stream plain white inline.svg';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user, userProfile } = useAuth();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const headerBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const searchBg = useColorModeValue('gray.100', 'gray.700');
  const searchFocusBg = useColorModeValue('gray.50', 'gray.600');
  const searchColor = useColorModeValue('gray.900', 'white');
  const searchPlaceholder = useColorModeValue('gray.500', 'gray.400');
  const tabColor = useColorModeValue('gray.600', 'gray.400');
  const activeTabColor = useColorModeValue('purple.600', 'purple.400');

  const logoSrc = useColorModeValue(lightLogo, darkLogo);

  return (
    <Box bg={headerBg} borderBottom="1px" borderColor={borderColor} boxShadow="sm" w="100%" maxW="100%" position="fixed" top={0} left={0} right={0} zIndex={2147483647} className="vindie-header" style={{ zIndex: 2147483647 }}>
      {/* Main Header */}
      <Flex align="center" justify="space-between" px={{ base: 3, sm: 4, md: 8 }} py={3} w="100%" maxW="100%">
        <Flex align="center" cursor="pointer" onClick={() => navigate('/')} gap={{ base: 1, sm: 2 }} minW="0" flexShrink={0}>
          <Image src={logoSrc} alt="Vindie Stream" height={{ base: '28px', md: '56px' }} />
        </Flex>
        
        {!isMobile && (
          <Flex align="center" gap={4} minW="0" flex="1" justify="flex-end" maxW="100%">
            <InputGroup maxW="400px" minW="200px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={searchPlaceholder} />
              </InputLeftElement>
              <Input
                placeholder="Search movies, series, books, creators..."
                bg={searchBg}
                border="none"
                color={searchColor}
                _placeholder={{ color: searchPlaceholder }}
                _focus={{ bg: searchFocusBg }}
                onClick={() => navigate('/search')}
                onKeyDown={(e) => {
                  const target = e.target as HTMLInputElement;
                  if (e.key === 'Enter' && target.value.trim()) {
                    navigate(`/search-enhanced?q=${encodeURIComponent(target.value.trim())}`);
                  }
                }}
              />
            </InputGroup>
            
            <HStack gap={2} flexShrink={0}>
              <Button
                size="sm"
                colorScheme="purple"
                variant="solid"
                onClick={() => navigate('/upload/category')}
              >
                Upload
              </Button>
            </HStack>
            
            <ThemeToggle />
            
            {user && <NotificationBell />}

            {user ? (
              <Menu>
                <MenuButton as={Button} variant="ghost" px={2} flexShrink={0}>
                  <Avatar 
                    size="sm" 
                    name={user.displayName || user.email || ''} 
                    src={userProfile?.avatarUrl || user.photoURL || undefined} 
                  />
                </MenuButton>
                <MenuList zIndex={2147483647}>
                  <MenuItem onClick={() => navigate('/profile')}>Profile</MenuItem>
                  <MenuItem onClick={() => navigate('/subscription-management')}>
                    Subscription
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      try {
                        await logout();
                        toast({ title: 'Signed out', status: 'success' });
                        navigate('/');
                      } catch (e: any) {
                        toast({ title: 'Sign out failed', description: e.message, status: 'error' });
                      }
                    }}
                  >
                    Logout
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <Flex align="center" gap={2} flexShrink={0}>
                <Button size="sm" variant="ghost" onClick={() => navigate('/login')}>Login</Button>
                <Button size="sm" colorScheme="purple" onClick={() => navigate('/signup')}>Sign up</Button>
              </Flex>
            )}
          </Flex>
        )}

        {isMobile && (
          <Flex align="center" gap={2} minW="0" flexShrink={0}>
            {user && <NotificationBell />}
            <IconButton
              aria-label="Search"
              icon={<SearchIcon />}
              variant="ghost"
              size="sm"
              onClick={() => navigate('/search')}
              flexShrink={0}
            />
            <IconButton
              aria-label="Menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              size="sm"
              onClick={onOpen}
              flexShrink={0}
            />
          </Flex>
        )}
      </Flex>

      {/* Mobile Menu Drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent maxW="320px" w="85%" ml="auto">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text fontSize="lg" fontWeight="bold">Menu</Text>
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4} mt={4}>
              {/* User Section */}
              {user ? (
                <Box p={4} bg="gray.50" _dark={{ bg: "gray.700" }} borderRadius="lg">
                  <Flex align="center" gap={3}>
                    <Avatar 
                      size="md" 
                      name={user.displayName || user.email || ''} 
                      src={userProfile?.avatarUrl || user.photoURL || undefined} 
                    />
                    <VStack align="start" gap={0}>
                      <Text fontWeight="bold" fontSize="sm">
                        {user.displayName || 'User'}
                      </Text>
                      <Text color="gray.500" fontSize="xs">
                        {user.email}
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
              ) : (
                <VStack align="stretch" gap={2}>
                  <Button
                    colorScheme="purple"
                    variant="solid"
                    onClick={() => {
                      onClose();
                      navigate('/login');
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    colorScheme="purple"
                    variant="outline"
                    onClick={() => {
                      onClose();
                      navigate('/signup');
                    }}
                  >
                    Sign Up
                  </Button>
                </VStack>
              )}

              {/* Navigation Links */}
              <VStack align="stretch" gap={2}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Content
                </Text>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={() => {
                    onClose();
                    navigate('/upload/category');
                  }}
                >
                  📤 Upload Content
                </Button>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={() => {
                    onClose();
                    navigate('/watchlist');
                  }}
                >
                  ⏰ Watchlist
                </Button>
              </VStack>

              {/* User Account Links */}
              {user && (
                <VStack align="stretch" gap={2}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Account
                  </Text>
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={() => {
                      onClose();
                      navigate('/profile');
                    }}
                  >
                    👤 Profile
                  </Button>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={() => {
                    onClose();
                    navigate('/subscription-management');
                  }}
                >
                  ⭐ Subscription
                </Button>
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={() => {
                      onClose();
                      navigate('/creator-dashboard');
                    }}
                  >
                    📊 Creator Dashboard
                  </Button>
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={() => {
                      onClose();
                      navigate('/analytics');
                    }}
                  >
                    📈 Analytics
                  </Button>
                </VStack>
              )}

              {/* Legal Links */}
              <VStack align="stretch" gap={2} pt={4} borderTop="1px" borderColor="gray.200" _dark={{ borderColor: "gray.600" }}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Legal
                </Text>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={() => {
                    onClose();
                    navigate('/privacy');
                  }}
                >
                  📄 Privacy Policy
                </Button>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={() => {
                    onClose();
                    navigate('/terms');
                  }}
                >
                  📋 Terms of Service
                </Button>
              </VStack>

              {/* Theme Toggle */}
              <Box pt={4} borderTop="1px" borderColor="gray.200" _dark={{ borderColor: "gray.600" }}>
                <Flex align="center" justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">Theme</Text>
                  <ThemeToggle />
                </Flex>
              </Box>

              {/* Logout Button */}
              {user && (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await logout();
                      toast({ title: 'Signed out', status: 'success' });
                      onClose();
                      navigate('/');
                    } catch (e: any) {
                      toast({ title: 'Sign out failed', description: e.message, status: 'error' });
                    }
                  }}
                >
                  Logout
                </Button>
              )}

              {/* Cancel / Close Drawer */}
              <Button
                variant="ghost"
                leftIcon={<CloseIcon />}
                onClick={onClose}
                aria-label="Close menu"
              >
                Close
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
